"use client";

import React, { useState, useEffect, Suspense } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { QuizAccountStep } from "../../components/QuizAccountStep";
import { useQuiz, useSelectedTherapist } from "../../contexts/AppContext";
import { useUser } from "../../contexts/UserContext";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { PublicTherapistProfile } from '@repo/supabase';
import { TIER_RATE_BOUNDS } from '@repo/booking';

interface QuizAnswers {
  ageVerification: 'yes' | 'no';
  sessionType: 'individual' | 'couples';
  therapistType: 'intern' | 'associate';
  rate: number;
  timezone: string;
  internAcknowledged?: boolean;
}

const steps = [
  {
    id: 'session-type',
    question: "What type of therapy are you looking for?",
    subtitle: "We offer both individual and couples therapy",
    type: 'single-select',
    options: [
      { id: 'individual', label: 'Individual', description: 'Personal therapy sessions' },
      { id: 'couples', label: 'Couples', description: 'Relationship therapy' }
    ]
  },
  {
    id: 'age-verification',
    question: "Are you 17 years or older and have completed high school?",
    subtitle: "Our services are available to individuals 17+ who have completed high school",
    type: 'single-select',
    options: [
      { id: 'yes', label: 'Yes', description: 'I am 17+ and have completed high school' },
      { id: 'no', label: 'No', description: 'I am under 17 or have not completed high school' }
    ]
  },
  {
    id: 'rate',
    question: "", // Will be dynamically set with therapist's name
    subtitle: "Adjust to what feels sustainable for you",
    type: 'slider',
    sliderConfig: {
      resident: {
        min: TIER_RATE_BOUNDS.resident.min,
        max: TIER_RATE_BOUNDS.resident.max,
        default: TIER_RATE_BOUNDS.resident.max,
      },
      associate: {
        min: TIER_RATE_BOUNDS.associate.min,
        max: TIER_RATE_BOUNDS.associate.max,
        default: TIER_RATE_BOUNDS.associate.max,
      },
    }
  },
  {
    id: 'timezone',
    question: "What's your time zone?",
    subtitle: "For scheduling purposes",
    type: 'single-select',
    options: [
      { id: 'America/Vancouver', label: 'Pacific Time (PT)', description: 'British Columbia' },
      { id: 'America/Edmonton', label: 'Mountain Time (MT)', description: 'Alberta, Northwest Territories' },
      { id: 'America/Winnipeg', label: 'Central Time (CST/CDT)', description: 'Manitoba, Nunavut (Central)' },
      { id: 'America/Regina', label: 'Saskatchewan Time', description: 'Saskatchewan (No DST, UTC-6 year-round)' },
      { id: 'America/Toronto', label: 'Eastern Time (EST/EDT)', description: 'Ontario, Quebec, Nunavut (Eastern)' },
      { id: 'America/Halifax', label: 'Atlantic Time (AST/ADT)', description: 'New Brunswick, Nova Scotia, Prince Edward Island' },
      { id: 'America/St_Johns', label: 'Newfoundland Time (NST/NDT)', description: 'Newfoundland and Labrador' }
    ]
  }
];

function QuickQuizPageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { quizAnswers: contextQuizAnswers, setQuizAnswers: setContextQuizAnswers } = useQuiz();
  const { selectedTherapist } = useSelectedTherapist();
  const { profile: userProfile, isLoadingProfile, userTimezone } = useUser();
  const therapistSlug = searchParams.get('therapist');
  const allowBookingParam = searchParams.get('allowBooking');
  const allowBooking = allowBookingParam === 'true';
  const partnershipId = searchParams.get('partnershipId');

  console.log('[QuickQuiz] URL search params:', searchParams.toString());
  console.log('[QuickQuiz] therapistSlug:', therapistSlug);
  console.log('[QuickQuiz] allowBooking param value:', allowBookingParam);
  console.log('[QuickQuiz] allowBooking boolean:', allowBooking);

  const [currentStep, setCurrentStep] = useState(() => {
    // Skip to the appropriate step if we already have certain data
    // Note: This runs before the component mounts, so we check initial values
    return 0; // Always start at session type
  });
  const [showAccount, setShowAccount] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInternDisclosure, setShowResidentDisclosure] = useState(false);
  const [isValidAccess, setIsValidAccess] = useState(false);
  const [therapistFromSlug, setTherapistFromSlug] = useState<PublicTherapistProfile | null>(null);
  const [answers, setAnswers] = useState<Partial<QuizAnswers>>({});

  // Authentication is handled by UserContext - userProfile will be populated if user is logged in
  
  // Fetch therapist data if we have a slug but no selected therapist
  useEffect(() => {
    async function fetchTherapist() {
      if (therapistSlug && !selectedTherapist) {
        try {
          const response = await fetch(`/api/therapists/${therapistSlug}`);
          if (response.ok) {
            const data = await response.json();
            setTherapistFromSlug(data);
          }
        } catch (error) {
          console.error('Failed to fetch therapist:', error);
        }
      }
    }
    
    fetchTherapist();
  }, [therapistSlug, selectedTherapist]);
  
  // Use either the selected therapist from context or the one fetched from slug
  const therapist = selectedTherapist || therapistFromSlug;
  
  // Check if there's a selected therapist and if this is for a intern therapist on mount
  useEffect(() => {
    // Redirect if no therapist is selected
    if (!therapist && !therapistSlug) {
      router.push('/therapists');
      return;
    }
    
    // Valid access
    setIsValidAccess(true);
    
    // Auto-populate therapist type if not already set
    if (!answers.therapistType && therapist?.therapistTier) {
      const therapistType = therapist.therapistTier === 'resident' ? 'intern' : 'associate';
      const rateConfig = therapistType === 'intern' ? { min: 45, max: 115 } : { min: 125, max: 200 };
      
      setAnswers(prev => ({ 
        ...prev, 
        therapistType,
        rate: prev.rate || rateConfig.max
      }));
    }
    
    if (answers.therapistType === 'intern' && !answers.internAcknowledged) {
      setShowResidentDisclosure(true);
    }
  }, [therapist, therapistSlug, router, answers.therapistType]);

  // Auto-populate timezone from logged-in user's profile
  useEffect(() => {
    if (!isLoadingProfile && userProfile && userTimezone && !answers.timezone) {
      setAnswers(prev => ({ ...prev, timezone: userTimezone }));
    }
  }, [isLoadingProfile, userProfile, userTimezone, answers.timezone]);

  // Filter out rate step if user is from a partnership
  const filteredSteps = partnershipId
    ? steps.filter(step => step.id !== 'rate')
    : steps;

  const currentStepData = filteredSteps[currentStep];
  const progress = ((currentStep + 1) / filteredSteps.length) * 100;
  
  // Determine therapist type - check both answers and therapist data
  const therapistType = answers.therapistType || 
                       (therapist?.therapistTier === 'resident' ? 'intern' : 
                        therapist?.therapistTier === 'associate' ? 'associate' : 'intern');
  
  const sliderConfigKey = therapistType === 'intern' ? 'resident' : 'associate';
  const sliderConfig = currentStepData?.sliderConfig?.[sliderConfigKey];
  
  // Debug logging
  console.log('Quick Quiz Debug:', {
    selectedTherapist: selectedTherapist?.therapistTier,
    therapistFromSlug: therapistFromSlug?.therapistTier,
    therapist: therapist?.therapistTier,
    answersTherapistType: answers.therapistType,
    computedTherapistType: therapistType,
    sliderConfigKey,
    sliderConfig,
    currentStep: currentStepData?.id
  });

  // Set default rate when reaching the rate step
  useEffect(() => {
    if (currentStepData?.id === 'rate' && !answers.rate && sliderConfig) {
      setAnswers(prev => ({ ...prev, rate: sliderConfig.default }));
    }
  }, [currentStep, currentStepData?.id, answers.rate, sliderConfig]);

  const handleSingleSelect = (field: keyof QuizAnswers, value: any) => {
    // If selecting couples and therapist is approved but NOT accepting couples, only update
    // local state (to show the warning message) but do NOT save to context.
    // This prevents a stale "couples" selection from persisting across visits.
    if (field === 'sessionType' && value === 'couples' && selectedTherapist &&
        selectedTherapist.isApprovedForCouples && !selectedTherapist.isAcceptingCouples && !allowBooking) {
      setAnswers(prev => ({ ...prev, sessionType: 'couples' }));
      return;
    }
    // Same guard for individual: therapist may be accepting couples-only right now
    // and closed to new individual intakes. Same UX — set local answer, don't persist,
    // don't auto-advance. The warning card + disabled Next button take it from there.
    if (field === 'sessionType' && value === 'individual' && selectedTherapist &&
        !selectedTherapist.isAcceptingIndividual && !allowBooking) {
      setAnswers(prev => ({ ...prev, sessionType: 'individual' }));
      return;
    }

    const updatedAnswers = { ...answers, [field]: value };
    setAnswers(updatedAnswers);

    // Save to context immediately
    setContextQuizAnswers({
      ...updatedAnswers,
      needs: [],
      insurance: '',
      availability: [],
      ...(partnershipId && { partnershipId })
    });

    // Don't auto-advance for rate slider
    if (field === 'rate') {
      return;
    }

    // Handle session type auto-advance logic
    if (field === 'sessionType') {
      // Auto-advance if Individual selected — but only when the therapist
      // (if pre-selected) is actually accepting individual clients. The
      // guard at the top of this function already short-circuited if not,
      // but the auto-advance path is separate.
      if (value === 'individual') {
        const canDoIndividual = !selectedTherapist || selectedTherapist.isAcceptingIndividual || allowBooking;
        if (canDoIndividual) {
          setTimeout(() => handleNext(), 300);
        }
        return;
      }

      // Auto-advance if Couples selected AND therapist supports couples
      if (value === 'couples' && selectedTherapist) {
        const canDoCouples = selectedTherapist.isApprovedForCouples && selectedTherapist.isAcceptingCouples;
        if (canDoCouples) {
          setTimeout(() => handleNext(), 300);
        }
        // Don't auto-advance if there will be a warning (not approved case)
      }
      return;
    }
    
    if (field === 'ageVerification' && value === 'no') {
      // Don't auto-advance if they're under 17
      return;
    }
    
    // Auto-advance after a brief delay to show selection for other single-select questions
    setTimeout(() => {
      handleNext();
    }, 300);
  };

  const handleNext = () => {
    if (currentStep < filteredSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Quiz complete, check authentication using UserContext
      // Wait for profile to finish loading before deciding
      if (isLoadingProfile) {
        // Still loading, wait a bit
        return;
      }

      if (userProfile) {
        // User is authenticated
        handleAuthSuccess();
      } else {
        // User is not authenticated, show account creation
        setShowAccount(true);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    if (!currentStepData) return false;
    
    switch (currentStepData?.id) {
      case 'session-type':
        // If couples is selected and therapist is not approved for couples, invalid
        if (answers.sessionType === 'couples' && selectedTherapist && !selectedTherapist.isApprovedForCouples) {
          return false;
        }
        return !!answers.sessionType;
      case 'age-verification':
        return !!answers.ageVerification;
      case 'rate':
        return !!answers.rate;
      case 'timezone':
        return !!answers.timezone;
      default:
        return false;
    }
  };

  const handleAuthSuccess = () => {
    // Build query params with quiz answers
    const queryParams = new URLSearchParams();

    if (answers.sessionType) {
      queryParams.append('sessionType', answers.sessionType);
    }
    if (answers.therapistType) {
      queryParams.append('therapistType', answers.therapistType);
    }
    if (answers.rate) {
      queryParams.append('rate', answers.rate.toString());
    }
    if (answers.timezone) {
      queryParams.append('timezone', answers.timezone);
    }
    // Preserve partnershipId parameter
    if (partnershipId) {
      queryParams.append('partnershipId', partnershipId);
    }
    // Preserve allowBooking parameter
    if (allowBooking) {
      queryParams.append('allowBooking', 'true');
    }

    const queryString = queryParams.toString();
    const urlSuffix = queryString ? `?${queryString}` : '';
    
    // Quiz data is already in context, just redirect to booking with params
    if (therapistSlug) {
      router.push(`/therapist/${therapistSlug}/book${urlSuffix}`);
    } else if (selectedTherapist) {
      // Generate slug from therapist name: firstname-lastname with spaces replaced by hyphens
      const firstName = (selectedTherapist.firstName || '').toLowerCase().replace(/\s+/g, '-');
      const lastName = (selectedTherapist.lastName || '').toLowerCase().replace(/\s+/g, '-');
      const slug = lastName ? `${firstName}-${lastName}` : firstName;
      router.push(`/therapist/${slug}/book${urlSuffix}`);
    } else {
      router.push('/therapists');
    }
  };


  // Don't render anything while checking if access is valid
  if (!isValidAccess) {
    return null;
  }

  // Show resident disclosure first if booking with a intern therapist
  if (showInternDisclosure && answers.therapistType === 'intern') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Simple Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-gray-100">
          <div className="container mx-auto px-4 py-4">
            <button
              onClick={() => router.push('/therapists')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to all therapists
            </button>
          </div>
        </div>

        {/* Friendly Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Hero Section */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full mb-4">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-3">
                Let's Make Sure We're The Right Fit!
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                You're interested in working with a <span className="font-semibold text-gray-800">Intern Therapist</span> — 
                that's great! Let's quickly check that they can best support your needs.
              </p>
            </div>

            {/* Two-Column Layout */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* What Intern Therapists Excel At */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900">Perfect For</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Anxiety & everyday stress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Depression & mood challenges</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Relationship & communication issues</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Life transitions & adjustments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Self-esteem & personal growth</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Work-life balance</span>
                  </li>
                </ul>
              </div>

              {/* Outside Their Scope */}
              <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 rounded-2xl border border-purple-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-lg text-gray-900">Outside Their Scope</h3>
                </div>
                <p className="text-sm text-purple-900 mb-3">Associates are needed for these specialized areas:</p>
                <ul className="space-y-2.5 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">◆</span>
                    <span className="text-gray-700">Crisis or emergency situations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">◆</span>
                    <span className="text-gray-700">Complex psychiatric conditions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">◆</span>
                    <span className="text-gray-700">Dissociative disorders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">◆</span>
                    <span className="text-gray-700">Legal proceeding support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">◆</span>
                    <span className="text-gray-700">Recent unprocessed trauma</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">◆</span>
                    <span className="text-gray-700">Untreated personality disorders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400 mt-0.5">◆</span>
                    <span className="text-gray-700">Substance dependence</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Simple Confirmation - Flat Layout */}
            <div className="mt-8">
              <label className="flex items-start gap-3 cursor-pointer mb-6">
                <input
                  type="checkbox"
                  id="acknowledgeScope"
                  checked={answers.internAcknowledged || false}
                  onChange={(e) => setAnswers(prev => ({ ...prev, internAcknowledged: e.target.checked }))}
                  className="mt-1 h-5 w-5 text-primary-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-primary-500 cursor-pointer"
                />
                <span className="text-gray-700 leading-relaxed">
                  The specialized situations above don't apply to me, and I'm seeking support for everyday challenges.
                </span>
              </label>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    if (answers.internAcknowledged) {
                      setShowResidentDisclosure(false);
                    }
                  }}
                  disabled={!answers.internAcknowledged}
                  className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all ${
                    answers.internAcknowledged
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue →
                </button>
                <button
                  onClick={() => router.push('/therapists')}
                  className="py-3 px-6 rounded-lg font-medium text-gray-600 hover:text-gray-800 transition-colors"
                >
                  See All Therapists
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showAccount) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <QuizAccountStep
            firstName=""
            email=""
            onComplete={handleAuthSuccess}
            isSubmitting={isSubmitting}
            customHeading="Final step to book your session"
            customSubheading={`Create your account to complete booking with ${selectedTherapist?.firstName || 'your therapist'}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-base font-semibold text-gray-900">Complete Your Booking</h1>
              <span className="text-sm text-gray-500">{currentStep + 1} of {filteredSteps.length}</span>
            </div>
            <button
              onClick={() => router.back()}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="min-h-[calc(100vh-140px)] bg-gradient-to-br from-purple-50/50 via-white to-blue-50/50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Question Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-8 mb-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {currentStepData?.id === 'rate' && sliderConfig 
                    ? `${therapist?.firstName || 'Your therapist'}'s standard rate is $${sliderConfig.max}`
                    : currentStepData?.question}
                </h2>
                <p className="text-gray-600">
                  {currentStepData?.subtitle}
                </p>
              </div>

              {/* Age verification */}
              {currentStepData?.id === 'age-verification' && (
                <div className="grid md:grid-cols-2 gap-4">
                  {currentStepData?.options?.map(option => (
                    <button
                      key={option.id}
                      onClick={() => handleSingleSelect('ageVerification', option.id)}
                      className={`p-6 rounded-xl border-2 transition-all text-center ${
                        answers.ageVerification === option.id
                          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50'
                          : 'border-gray-200 hover:border-purple-300 bg-gray-50 hover:bg-white'
                      }`}
                    >
                  <h3 className="font-semibold text-lg text-gray-900 mb-1">{option.label}</h3>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </button>
              ))}
                  {answers.ageVerification === 'no' && (
                    <div className="md:col-span-2 mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-amber-800 mb-1">
                            Age requirement not met
                          </p>
                          <p className="text-sm text-amber-700 mb-3">
                            Our services are currently only available for individuals 17 years and older.
                          </p>
                          <div className="p-3 bg-white rounded-lg border border-amber-100">
                            <p className="text-sm font-medium text-amber-800 mb-1">If you need immediate support:</p>
                            <p className="text-sm text-amber-700">
                              Kids Help Phone: <span className="font-semibold">1-800-668-6868</span><br />
                              Text: <span className="font-semibold">CONNECT to 686868</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
            </div>
          )}

              {/* Session type */}
              {currentStepData?.id === 'session-type' && (
                <div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {currentStepData?.options?.map(option => (
                      <button
                        key={option.id}
                        onClick={() => handleSingleSelect('sessionType', option.id)}
                        className={`p-6 rounded-xl border-2 transition-all text-center ${
                          answers.sessionType === option.id
                            ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50'
                            : 'border-gray-200 hover:border-purple-300 bg-gray-50 hover:bg-white'
                        }`}
                      >
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{option.label}</h3>
                    <p className="text-sm text-gray-600">{option.description}</p>
                  </button>
                ))}
              </div>
              
              {((): React.ReactNode => {
                if (answers.sessionType !== 'couples') return null;
                if (!selectedTherapist) return null;
                if (selectedTherapist.isApprovedForCouples) return null;
                const firstName = (selectedTherapist.firstName ?? '') as string;
                return (
                    <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                      <p className="text-sm font-semibold text-amber-800 mb-1">
                        {firstName} doesn't offer couples therapy
                      </p>
                      <p className="text-sm text-amber-700 mb-3">
                        {firstName} specializes in individual therapy. To continue booking with them, please select "Individual" above.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleSingleSelect('sessionType', 'individual')}
                          className="text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Switch to Individual
                        </button>
                        <button
                          onClick={() => router.push('/therapists')}
                          className="text-sm font-medium text-amber-800 hover:text-amber-900 underline"
                        >
                          Find couples therapists
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })()}

              {/* Show message if therapist is approved but not accepting couples (unless allowBooking override) */}
              {Boolean(answers.sessionType === 'couples' && selectedTherapist &&
                   selectedTherapist.isApprovedForCouples && !selectedTherapist.isAcceptingCouples && !allowBooking) && (
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                      <p className="text-sm font-semibold text-blue-800 mb-1">
                        {(selectedTherapist?.firstName ?? '') as string} is not currently accepting couples clients
                      </p>
                      <p className="text-sm text-blue-700">
                        We have other excellent couples therapists available now who would be a great fit.
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={() => router.push('/therapists')}
                          className="text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          See available therapists
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Same warning for individual: therapist may be closed to new individual clients
                  (e.g. couples-only cohort). Offer to switch to couples if approved, or send them
                  to the /therapists list. */}
              {Boolean(answers.sessionType === 'individual' && selectedTherapist &&
                   !selectedTherapist.isAcceptingIndividual && !allowBooking) && (
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200">
                      <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-blue-800 mb-1">
                            {(selectedTherapist?.firstName ?? '') as string} is not currently accepting new individual clients
                          </p>
                          <p className="text-sm text-blue-700">
                            {selectedTherapist?.isApprovedForCouples && selectedTherapist?.isAcceptingCouples
                              ? `They are still taking couples clients. Otherwise, we have other individual therapists available now.`
                              : `We have other individual therapists available now who would be a great fit.`}
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {Boolean(selectedTherapist?.isApprovedForCouples && selectedTherapist?.isAcceptingCouples) && (
                              <button
                                onClick={() => handleSingleSelect('sessionType', 'couples')}
                                className="text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 px-3 py-1.5 rounded-lg transition-colors"
                              >
                                Switch to Couples
                              </button>
                            )}
                            <button
                              onClick={() => router.push('/therapists')}
                              className="text-sm font-medium text-blue-800 hover:text-blue-900 underline"
                            >
                              See available therapists
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
            </div>
          )}

          {/* Rate slider */}
          {currentStepData?.type === 'slider' && sliderConfig && (
            <div className="px-4">
              {/* Professional Gradient Slider */}
              <div className="relative pt-12 pb-8">
                {/* Floating value display */}
                <div 
                  className="absolute -top-2 transition-all duration-200 ease-out"
                  style={{
                    left: `${((answers.rate || sliderConfig.default) - sliderConfig.min) / (sliderConfig.max - sliderConfig.min) * 100}%`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  <div className="relative">
                    <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                      ${answers.rate || sliderConfig.default}
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-2">
                      <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-purple-600"></div>
                    </div>
                  </div>
                </div>

                {/* Slider container */}
                <div className="relative mt-8">
                  {/* Background track */}
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full shadow-inner"></div>
                  
                  {/* Progress track */}
                  <div 
                    className="absolute left-0 top-1/2 -translate-y-1/2 h-4 bg-gradient-to-r from-blue-400 via-blue-500 to-purple-600 rounded-full shadow-sm transition-all duration-200"
                    style={{
                      width: `${((answers.rate || sliderConfig.default) - sliderConfig.min) / (sliderConfig.max - sliderConfig.min) * 100}%`
                    }}
                  >
                    {/* Subtle shimmer effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 rounded-full"></div>
                  </div>

                  {/* Actual range input */}
                  <input
                    type="range"
                    min={sliderConfig.min}
                    max={sliderConfig.max}
                    value={answers.rate || sliderConfig.default}
                    onChange={(e) => handleSingleSelect('rate', parseInt(e.target.value))}
                    className="relative z-10 w-full h-4 bg-transparent appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-10 [&::-webkit-slider-thumb]:h-10 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gradient-to-br [&::-webkit-slider-thumb]:from-white [&::-webkit-slider-thumb]:to-gray-50 [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(59,130,246,0.2),0_6px_16px_rgba(0,0,0,0.15)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-200 [&::-webkit-slider-thumb:hover]:scale-110 [&::-webkit-slider-thumb:hover]:shadow-[0_0_0_5px_rgba(59,130,246,0.3),0_8px_20px_rgba(0,0,0,0.2)] [&::-webkit-slider-thumb:active]:cursor-grabbing [&::-webkit-slider-thumb:active]:scale-105 [&::-moz-range-thumb]:w-10 [&::-moz-range-thumb]:h-10 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gradient-to-br [&::-moz-range-thumb]:from-white [&::-moz-range-thumb]:to-gray-50 [&::-moz-range-thumb]:shadow-[0_0_0_3px_rgba(59,130,246,0.2),0_6px_16px_rgba(0,0,0,0.15)] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:transition-all [&::-moz-range-thumb]:duration-200 [&::-moz-range-thumb:hover]:scale-110 [&::-moz-range-thumb:hover]:shadow-[0_0_0_5px_rgba(59,130,246,0.3),0_8px_20px_rgba(0,0,0,0.2)] [&::-moz-range-thumb:active]:cursor-grabbing [&::-moz-range-thumb:active]:scale-105"
                  />
                </div>

                {/* Slider labels */}
                <div className="flex justify-between mt-8">
                  <div>
                    <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Sliding scale</div>
                    <div className="text-lg font-bold text-gray-800">${sliderConfig.min}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Standard rate</div>
                    <div className="text-lg font-bold text-gray-800">${sliderConfig.max}</div>
                  </div>
                </div>
              </div>

              {/* Enhanced payment display */}
              <div className="mt-12 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 border border-purple-100">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Your Rate</p>
                  
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      ${answers.rate || sliderConfig.default}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 font-medium">per 50-minute session</p>
                </div>
              </div>
            </div>
          )}

              {/* Timezone */}
              {currentStepData?.id === 'timezone' && (
                <div className="grid md:grid-cols-2 gap-3">
                  {currentStepData?.options?.map(option => (
                    <button
                      key={option.id}
                      onClick={() => handleSingleSelect('timezone', option.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        answers.timezone === option.id
                          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-blue-50'
                          : 'border-gray-200 hover:border-purple-300 bg-gray-50 hover:bg-white'
                      }`}
                    >
                  <h3 className="font-medium text-gray-900">{option.label}</h3>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </button>
              ))}
            </div>
          )}

              {/* Navigation */}
              <div className="mt-8 flex justify-between pt-6 border-t border-gray-100">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    currentStep === 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary-600'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>
                <button
                  onClick={handleNext}
                  disabled={
                    !isStepValid() ||
                    answers.ageVerification === 'no' ||
                    (answers.sessionType === 'couples' && !!selectedTherapist?.isApprovedForCouples && !selectedTherapist?.isAcceptingCouples && !allowBooking) ||
                    (answers.sessionType === 'individual' && !!selectedTherapist && !selectedTherapist.isAcceptingIndividual && !allowBooking)
                  }
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    !isStepValid() ||
                    answers.ageVerification === 'no' ||
                    (answers.sessionType === 'couples' && !!selectedTherapist?.isApprovedForCouples && !selectedTherapist?.isAcceptingCouples && !allowBooking) ||
                    (answers.sessionType === 'individual' && !!selectedTherapist && !selectedTherapist.isAcceptingIndividual && !allowBooking)
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                  }`}
                >
                  {currentStep === filteredSteps.length - 1 ? 'Continue' : 'Next'}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function QuickQuizPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading quiz...</p>
        </div>
      </div>
    }>
      <QuickQuizPageContent />
    </Suspense>
  );
}